import { FormEvent, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useScrollLock } from "@/lib/scrollLock";

interface Props {
  open: boolean;
  onSubmit: (nickname: string) => void;
}

const MIN = 2;
const MAX = 15;

export const NicknameModal = ({ open, onSubmit }: Props): JSX.Element | null => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(id);
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  const trimmed = value.trim();
  const valid = trimmed.length >= MIN && trimmed.length <= MAX;
  const hint = !valid
    ? t("modalNickname.hintMin", { min: MIN, defaultValue: `Au moins ${MIN} caractères` })
    : t("modalNickname.hintCount", {
        n: trimmed.length,
        max: MAX,
        defaultValue: `${trimmed.length} / ${MAX}`,
      });

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!valid) return;
    onSubmit(trimmed);
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="nickname-title"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-ink/80 animate-fade-in" aria-hidden="true" />

      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-sm bg-bg rounded-modal shadow-warm-lg p-6 animate-scale-in"
      >
        <h2
          id="nickname-title"
          className="display-font text-xl font-semibold text-ink leading-tight text-center"
        >
          {t("modalNickname.title", "Pseudonyme")}
        </h2>

        <p className="mt-3 text-sm text-ink-soft leading-snug text-center">
          {t(
            "modalNickname.description",
            "Votre pseudo permet de distinguer vos plats dans le panier partagé. Il n'est pas transmis au restaurant."
          )}
        </p>

        <div className="mt-5">
          <input
            ref={inputRef}
            type="text"
            inputMode="text"
            autoComplete="off"
            maxLength={MAX}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t("modalNickname.placeholder", "Votre pseudo")}
            aria-label={t("modalNickname.ariaLabel", "Pseudo")}
            aria-invalid={!valid}
            className="w-full px-4 py-3 rounded-button border-2 border-border bg-surface text-ink text-base outline-none focus:border-primary transition-colors"
          />
          <p
            className={`mt-1.5 text-xs ${
              valid ? "text-ink-muted" : "text-ink-soft"
            }`}
          >
            {hint}
          </p>
        </div>

        <button
          type="submit"
          disabled={!valid}
          className="mt-5 w-full bg-primary text-white rounded-button py-3 px-5 font-semibold shadow-warm transition-colors hover:bg-primary-hover disabled:opacity-40"
        >
          {t("modalNickname.cta", "Continuer")}
        </button>
      </form>
    </div>,
    document.body
  );
};
